/**
 * MA UI WebSocket client.
 *
 * V2 has no live WebSocket endpoint. Environment selection MUST use explicit
 * REACT_APP_ENABLE_WS / REACT_APP_WS_URL — never NODE_ENV (CRA sets
 * NODE_ENV=production for staging builds).
 *
 * When WS is disabled, this module must not open a socket and must not put
 * an Auth0 / Support Mode token on a WebSocket URL.
 */

const ENABLE_FLAG = "REACT_APP_ENABLE_WS";
const URL_FLAG = "REACT_APP_WS_URL";

let wsReconnectTimer;
let activeSocket = null;
let tokenSource = null;
let connectGeneration = 0;

export function isWebSocketEnabled(env = process.env) {
  return String(env[ENABLE_FLAG] ?? "").trim().toLowerCase() === "true";
}

export function resolveWebSocketUrl(env = process.env) {
  if (!isWebSocketEnabled(env)) {
    return null;
  }
  const url = String(env[URL_FLAG] ?? "").trim();
  if (!url) {
    return null;
  }
  return url;
}

export function buildAuthenticatedWebSocketUrl(token, env = process.env) {
  const base = resolveWebSocketUrl(env);
  if (!base || token == null || token === "") {
    return null;
  }
  const separator = base.includes("?") ? "&" : "?";
  return `${base}${separator}token=${encodeURIComponent(token)}`;
}

const resolveToken = async () => {
  if (!tokenSource) {
    return null;
  }

  if (typeof tokenSource === "function") {
    try {
      return await tokenSource();
    } catch (error) {
      console.error("WebSocket token provider failed:", error);
      return null;
    }
  }

  return tokenSource;
};

const scheduleReconnect = () => {
  if (!isWebSocketEnabled() || !resolveWebSocketUrl()) {
    return;
  }
  if (wsReconnectTimer) {
    return;
  }

  wsReconnectTimer = setTimeout(() => {
    wsReconnectTimer = null;
    void connectWebSocket();
  }, 5000);
  if (typeof wsReconnectTimer.unref === "function") {
    wsReconnectTimer.unref();
  }
};

const connectWebSocket = async () => {
  if (!isWebSocketEnabled()) {
    return;
  }

  const generation = connectGeneration;
  const token = await resolveToken();
  if (generation !== connectGeneration) {
    return;
  }
  if (!token) {
    console.warn("WebSocket connect skipped: missing auth token");
    scheduleReconnect();
    return;
  }

  const url = buildAuthenticatedWebSocketUrl(token);
  if (!url) {
    console.warn("WebSocket connect skipped: REACT_APP_WS_URL is not configured");
    return;
  }

  const ws = new WebSocket(url);
  activeSocket = ws;

  ws.onopen = () => {
    console.log("WebSocket connected");
    if (wsReconnectTimer) {
      clearTimeout(wsReconnectTimer);
      wsReconnectTimer = null;
    }
  };

  ws.onclose = () => {
    scheduleReconnect();
  };

  ws.onerror = (error) => {
    console.log("WebSocket error, will attempt to reconnect:", error);
  };
};

const setupWebSocket = (authTokenOrProvider) => {
  if (!isWebSocketEnabled()) {
    return null;
  }

  if (authTokenOrProvider !== undefined) {
    tokenSource = authTokenOrProvider;
  }

  if (!resolveWebSocketUrl()) {
    console.warn("WebSocket enabled but REACT_APP_WS_URL is empty; connection skipped");
    return null;
  }

  if (
    activeSocket &&
    (activeSocket.readyState === WebSocket.OPEN ||
      activeSocket.readyState === WebSocket.CONNECTING)
  ) {
    return activeSocket;
  }

  void connectWebSocket();
  return activeSocket;
};

/** App entry: never invokes setupWebSocket when V2 WS is disabled. */
export function maybeSetupWebSocket(authTokenOrProvider) {
  if (!isWebSocketEnabled()) {
    return null;
  }
  return setupWebSocket(authTokenOrProvider);
}

export function __resetWebSocketForTests() {
  connectGeneration += 1;
  if (wsReconnectTimer) {
    clearTimeout(wsReconnectTimer);
    wsReconnectTimer = null;
  }
  if (activeSocket && typeof activeSocket.close === "function") {
    try {
      activeSocket.close();
    } catch {
      /* ignore */
    }
  }
  activeSocket = null;
  tokenSource = null;
}

export default setupWebSocket;
